import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Box, Heading, useToast, Select, Button, Alert, AlertIcon } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const ModifyQuestionsToPaperSet = () => {
  const [paperSets, setPaperSets] = useState([]);
  const [selectedPaperSetId, setSelectedPaperSetId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const gridRef = useRef();

  const gridStyle = useMemo(() => ({ width: '100%', height: '400px' }), []);

  useEffect(() => {
    const fetchPaperSets = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/PaperSet`);
        setPaperSets(Array.isArray(response.data) ? response.data : response.data.$values || []);
      } catch (error) {
        setError('Error fetching paper sets');
        console.error('Error fetching paper sets:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Questions`);
        setQuestions(Array.isArray(response.data) ? response.data : response.data.$values || []);
      } catch (error) {
        setError('Error fetching questions');
        console.error('Error fetching questions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaperSets();
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (selectedPaperSetId) {
      const fetchPaperSetDetails = async () => {
        setLoading(true);
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/PaperSet/${selectedPaperSetId}`);
          const currentQuestions = response.data.paperSetQuestions
            ? (Array.isArray(response.data.paperSetQuestions)
              ? response.data.paperSetQuestions.map((q) => q.questionId)
              : (response.data.paperSetQuestions.$values || []).map((q) => q.questionId))
            : [];
          setSelectedQuestions(currentQuestions);
        } catch (error) {
          setError('Error fetching paper set details');
          console.error('Error fetching paper set details:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchPaperSetDetails();
    }
  }, [selectedPaperSetId]);

  const handlePaperSetChange = (e) => {
    setSelectedPaperSetId(e.target.value);
  };

  const handleQuestionSelection = (event) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedIds = selectedNodes.map((node) => node.data.questionId);
    setSelectedQuestions(selectedIds);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/PaperSet/${selectedPaperSetId}/questions`, {
        questionIds: selectedQuestions,
      });
      toast({
        title: 'Paper set updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      const errorMessage = error.response?.data || error.message;
      toast({
        title: 'Error updating paper set',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const columnDefs = useMemo(() => [
    { headerCheckboxSelection: true, checkboxSelection: true, width: 50 },
    { headerName: 'Series', valueGetter: 'node.rowIndex + 1', width: 90 },
    { headerName: 'Question', field: 'questionText', flex: 1 },
    {
      headerName: 'Correct Answer',
      field: 'correctAnswer',
      valueGetter: (params) => {
        const correctOption = params.data.options?.find((option) => option.isCorrect);
        return correctOption ? correctOption.optionText : 'N/A';
      },
      flex: 1,
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
  }), []);

  const rowData = useMemo(() => questions.map((question) => ({
    questionId: question.questionId,
    questionText: question.questionText,
    options: question.options?.$values || question.options || [],
  })), [questions]);

  return (
    <Box className="relative lg:w-[80%] md:w-3/4 w-full mx-auto mt-10">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-300 to-blue-600 shadow-lg transform -skew-y-12 sm:skew-y-0 sm:-rotate-[8deg] rounded-3xl drop-shadow-md"></div>

      <Box className="relative bg-white rounded-2xl drop-shadow-md p-8 flex flex-col mt-10 space-y-6 z-10">
        <div className="flex items-center gap-4 border-b pb-3 border-gray-300">
          <h1 className="text-2xl font-bold mb-6">Assign Questions to Paper Set</h1>
        </div>

        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Select placeholder="Select Paper Set" onChange={handlePaperSetChange} mb={6}>
          {paperSets.map((paperSet) => (
            <option key={paperSet.paperSetId} value={paperSet.paperSetId}>
              {paperSet.title}
            </option>
          ))}
        </Select>

        <Heading size="md" mb={4}>Default Question List</Heading>
        <div className="ag-theme-alpine" style={gridStyle}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            onSelectionChanged={handleQuestionSelection}
            pagination={true}
            paginationPageSize={10}
          />
        </div>

        <Button type="submit" colorScheme="blue" onClick={handleSubmit} w="full" mt={4}>
          Save Questions to Paper Set
        </Button>
      </Box>
    </Box>
  );
};


export default ModifyQuestionsToPaperSet;
